import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../axiosInstance';

// GET /dashboard/overview — aggregated live metrics for the Dashboard landing
// page. `period` ({from, to}) scopes the analytics; omit it for the running
// month. Toasts are the calling screen's responsibility.
export const useDashboardOverview = (period) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const from = period?.from;
  const to = period?.to;

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/dashboard/overview', {
        params: { _t: Date.now(), ...(from && to ? { from, to } : {}) },
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (res.data?.success) {
        setData(res.data.data);
        return { success: true };
      }
      const message = res.data?.message || 'Failed to load dashboard data';
      setError(message);
      return { success: false, message };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to load dashboard data';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return { data, loading, error, refetch: fetchOverview };
};

// GET /dashboard/online-bookings — latest online (website) bookings for the
// header notification bell. Polls on an interval so new bookings surface without
// a page reload. Read/unread state is tracked client-side by the bell component.
export const useOnlineBookings = (pollMs = 60000) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/dashboard/online-bookings', {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (res.data?.success && Array.isArray(res.data.data)) {
        setBookings(res.data.data);
      }
    } catch {
      // Silent — the bell simply keeps its last known list on a transient error.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    if (!pollMs) return undefined;
    const t = setInterval(fetchBookings, pollMs);
    return () => clearInterval(t);
  }, [fetchBookings, pollMs]);

  return { bookings, loading, refetch: fetchBookings };
};

// GET /dashboard/notifications — unified header-bell feed (refunds to process,
// unpaid-before-travel, supplier-due-before-travel, new online bookings). Each
// item is a typed notification; the bell tracks read/unread client-side.
export const useNotifications = (pollMs = 60000) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/dashboard/notifications', {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (res.data?.success && Array.isArray(res.data.data)) setItems(res.data.data);
    } catch {
      // Silent — keep the last known list on a transient error.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    if (!pollMs) return undefined;
    const t = setInterval(fetchItems, pollMs);
    return () => clearInterval(t);
  }, [fetchItems, pollMs]);

  return { items, loading, refetch: fetchItems };
};
